import React, { useState, useRef } from 'react';
import { Box, Button, Typography, Alert, LinearProgress, Stack, Chip } from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Description as DescriptionIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
  Visibility as PreviewIcon,
} from '@mui/icons-material';
import CustomCard from '../../Shared/Components/CustomCard/CustomCard';
import OcrPreviewModal from './OcrPreviewModal';
import { validateFile, formatFileSize } from '../Utils/fileValidation';
import { useOcrProcessing, OcrResult } from '../Hooks/useOcrProcessing';
import { UserInvoiceCorrections } from '../Types/userInvoiceCorrections';

interface FileUploadZoneProps {
  onFileSelect: (file: File, corrections?: UserInvoiceCorrections) => void;
  isLoading?: boolean;
  error?: string | null;
  enableOcrPreview?: boolean; // Enable OCR preview for images
}

/**
 * File upload component for invoice import
 * Supports click-to-upload with file validation and optional OCR preview
 */
const FileUploadZone: React.FC<FileUploadZoneProps> = ({
  onFileSelect,
  isLoading = false,
  error,
  enableOcrPreview = true,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [showOcrModal, setShowOcrModal] = useState(false);
  const [ocrResult, setOcrResult] = useState<OcrResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { processImage, isProcessing, progress, error: ocrError } = useOcrProcessing();

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Reset previous errors
    setValidationError(null);

    // Validate file
    const validation = validateFile(file);
    if (!validation.isValid) {
      setValidationError(validation.errorMessage || 'Nieprawidłowy plik');
      setSelectedFile(null);
      return;
    }

    // File is valid
    setSelectedFile(file);
    setValidationError(null);
  };

  const handleUploadClick = async () => {
    if (!selectedFile) return;

    // Check if file is image and OCR preview is enabled
    const isImage = selectedFile.type.startsWith('image/');
    if (isImage && enableOcrPreview && !ocrResult) {
      // Run OCR first
      try {
        const result = await processImage(selectedFile);
        setOcrResult(result);
        setShowOcrModal(true);
      } catch (err) {
        // OCR failed, continue with direct upload
        onFileSelect(selectedFile);
      }
    } else {
      // Direct upload (non-image or OCR already done)
      onFileSelect(selectedFile);
    }
  };

  const handleOcrConfirm = (corrections: UserInvoiceCorrections) => {
    if (selectedFile) {
      setShowOcrModal(false);
      onFileSelect(selectedFile, corrections);
    }
  };

  const handleSkipOcr = () => {
    if (selectedFile) {
      setShowOcrModal(false);
      onFileSelect(selectedFile);
    }
  };

  const handleCloseOcrModal = () => {
    setShowOcrModal(false);
    setOcrResult(null);
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  const handleClearFile = () => {
    setSelectedFile(null);
    setValidationError(null);
    setOcrResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const getFileIcon = (file: File) => {
    if (file.type.startsWith('image/')) {
      return <ImageIcon fontSize="large" color="primary" />;
    }
    if (file.type === 'application/pdf') {
      return <PdfIcon fontSize="large" color="error" />;
    }
    return <DescriptionIcon fontSize="large" color="action" />;
  };

  const displayError = validationError || error || ocrError;
  const isProcessingOrLoading = isLoading || isProcessing;
  const isImageFile = selectedFile?.type.startsWith('image/');

  return (
    <>
      <CustomCard
        title="Import faktury"
        subheader="Prześlij plik faktury w formacie: JPEG, PNG, TIFF, PDF, XML, CSV, JSON (max 10 MB)"
        icon={<CloudUploadIcon />}
      >
        <Stack spacing={3}>
          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".jpg,.jpeg,.png,.tiff,.tif,.pdf,.xml,.csv,.json"
            onChange={handleFileInputChange}
            style={{ display: 'none' }}
            disabled={isProcessingOrLoading}
          />

          {/* OCR Progress */}
          {isProcessing && progress && (
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {progress.status}
              </Typography>
              <LinearProgress variant="determinate" value={progress.progress} />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                {progress.progress.toFixed(0)}%
              </Typography>
            </Box>
          )}

          {/* Upload zone */}
          <Box
            sx={{
              border: '2px dashed',
              borderColor: displayError ? 'error.main' : 'divider',
              borderRadius: 2,
              p: 4,
              textAlign: 'center',
              bgcolor: displayError ? 'error.lighter' : 'background.paper',
              cursor: isProcessingOrLoading ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s',
              '&:hover': {
                borderColor: isProcessingOrLoading ? 'divider' : 'primary.main',
                bgcolor: isProcessingOrLoading ? 'background.paper' : 'action.hover',
              },
            }}
            onClick={!isProcessingOrLoading && !selectedFile ? handleBrowseClick : undefined}
          >
            {selectedFile ? (
              <Stack spacing={2} alignItems="center">
                {getFileIcon(selectedFile)}
                <Typography variant="body1" fontWeight="medium">
                  {selectedFile.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatFileSize(selectedFile.size)}
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Chip
                    label={selectedFile.type || 'Nieznany typ'}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                </Stack>
              </Stack>
            ) : (
              <Stack spacing={2} alignItems="center">
                <CloudUploadIcon sx={{ fontSize: 64, color: 'text.secondary' }} />
                <Typography variant="h6" color="text.secondary">
                  Kliknij, aby wybrać plik
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Obsługiwane formaty: JPEG, PNG, TIFF, PDF, XML (KSeF), CSV, JSON
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Maksymalny rozmiar: 10 MB
                </Typography>
              </Stack>
            )}
          </Box>

          {/* Multi-page info alert */}
          <Alert severity="info" icon={false} sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Wielostronicowe faktury:</strong> Jeśli faktura składa się z kilku
              zdjęć/stron, połącz je w jeden plik PDF lub TIFF przed przesłaniem. System nie
              obsługuje scalania osobnych obrazków.
            </Typography>
          </Alert>

          {/* Error alert */}
          {displayError && (
            <Alert severity="error" onClose={() => setValidationError(null)}>
              {displayError}
            </Alert>
          )}

          {/* Loading indicator */}
          {isLoading && (
            <Box>
              <LinearProgress />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Przesyłanie pliku...
              </Typography>
            </Box>
          )}

          {/* Action buttons */}
          <Stack direction="row" spacing={2} justifyContent="center">
            {selectedFile && !isProcessingOrLoading && (
              <>
                <Button variant="outlined" onClick={handleClearFile}>
                  Anuluj
                </Button>
                {isImageFile && enableOcrPreview && (
                  <Button
                    variant="outlined"
                    startIcon={<PreviewIcon />}
                    onClick={handleUploadClick}
                    color="secondary"
                  >
                    Podgląd OCR
                  </Button>
                )}
                <Button
                  variant="contained"
                  startIcon={<CloudUploadIcon />}
                  onClick={
                    isImageFile && enableOcrPreview
                      ? handleUploadClick
                      : () => onFileSelect(selectedFile)
                  }
                  disabled={isProcessingOrLoading}
                >
                  {isImageFile && enableOcrPreview ? 'Prześlij z OCR' : 'Prześlij i importuj'}
                </Button>
              </>
            )}
            {!selectedFile && !isProcessingOrLoading && (
              <Button
                variant="contained"
                startIcon={<CloudUploadIcon />}
                onClick={handleBrowseClick}
              >
                Wybierz plik
              </Button>
            )}
          </Stack>
        </Stack>
      </CustomCard>

      {/* OCR Preview Modal */}
      <OcrPreviewModal
        open={showOcrModal}
        onClose={handleCloseOcrModal}
        ocrResult={ocrResult}
        file={selectedFile}
        onConfirm={handleOcrConfirm}
        onSkipOcr={handleSkipOcr}
      />
    </>
  );
};

export default FileUploadZone;
